import { Box, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material'
import type { EventSummary } from '../api/generated/EventSummary'
import { formatRange } from '../shifts/time'
import { eventWhen, fillPercent } from './summaries'

const roleLabels = { organizer: 'Organisateur', volunteer: 'Bénévole' } as const

function Figure({
  label,
  value,
  urgent = false,
}: {
  label: string
  value: string
  urgent?: boolean
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 800,
          lineHeight: 1.2,
          color: urgent ? 'warning.dark' : undefined,
        }}
      >
        {value}
      </Typography>
    </Box>
  )
}

function Figures({ figures }: { figures: NonNullable<EventSummary['figures']> }) {
  const percent = fillPercent(figures)
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={2}>
        <Figure label="Créneaux pourvus" value={percent === undefined ? '—' : `${percent} %`} />
        <Figure
          label="Bénévoles à répartir"
          value={String(figures.volunteers_to_assign)}
          urgent={figures.volunteers_to_assign > 0}
        />
      </Stack>
      {percent !== undefined && (
        <LinearProgress
          variant="determinate"
          value={percent}
          aria-label="Places pourvues"
          sx={{ height: 6, borderRadius: 1 }}
        />
      )}
    </Stack>
  )
}

function NextShift({ shift }: { shift: EventSummary['next_shift'] }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
        Mon prochain créneau
      </Typography>
      {shift ? (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {shift.position_name} · {formatRange(shift.starts_at, shift.ends_at)}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Aucun créneau à venir
        </Typography>
      )}
    </Box>
  )
}

/** An event of the home page, with what the user's role needs at a glance. */
export function EventCard({ event, now }: { event: EventSummary; now: Date }) {
  const when = eventWhen(event, now)
  return (
    <Paper
      variant="outlined"
      component="a"
      href={`/events/${event.slug}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: { xs: 1.75, md: 2.5 },
        color: 'inherit',
        textDecoration: 'none',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" component="h2" sx={{ overflowWrap: 'anywhere' }}>
            {event.name}
          </Typography>
          {when && (
            <Typography variant="body2" color="text.secondary">
              {when}
            </Typography>
          )}
        </Box>
        <Chip
          size="small"
          label={roleLabels[event.role]}
          sx={event.role === 'organizer' ? { bgcolor: 'primary.light', color: 'primary.main' } : {}}
        />
      </Stack>
      <Typography
        variant="body2"
        color={event.applications_open ? 'success.main' : 'text.secondary'}
        sx={{ fontWeight: 600 }}
      >
        {event.applications_open ? 'Inscriptions ouvertes' : 'Inscriptions fermées'}
      </Typography>
      {event.figures && <Figures figures={event.figures} />}
      {event.role === 'volunteer' && <NextShift shift={event.next_shift} />}
    </Paper>
  )
}
